import React, { Fragment, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useFormikContext } from "formik";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import {
  formatUploadedAttachment,
  prepareCreateAttachments
} from "utils/attachment";
import { useAttachment, useCtxReferences } from "utils/selectors";
import FilePicker from "components/FilePicker";
import {
  ATTACHMENT_CONTEXT_TYPE,
  PICKER_OPTIONS,
  PROMPTS
} from "utils/constants/attachment";
import LoadingSpinner from "components/LoadingSpinner";
import { createAttachment, fetchAttachments } from "actions/attachment";
import { getUUID } from "utils/uuid";
import { ensureAttachments } from "utils/hooks";
import { prepareSchemaImageFromAttachment } from "utils/schema";
import { formFactorAction } from "utils/view";
import HoverIconButton from "components/HoverIconButton";
import { COLORS } from "utils/styles";

const DIMENSIONS = {
  width: 80,
  height: 80
};
const IMAGE_LOADER_STYLE = { minHeight: 164 };
const DEFAULT_UPLOAD_PROMPT = "Upload an image for your profile";

/**
 * ImageField is a wrapper around FilePicker
 */
const ImageField = ({ image, name, actions, uploadPrompt }) => {
  const { touched, setFieldValue, setFieldTouched } = useFormikContext();
  const { resourceID, resourceUUID, resource } = useCtxReferences();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const prompt = useMemo(() => {
    const imagePrompt = cloneDeep(PROMPTS.IMAGES);
    imagePrompt.upload.header = uploadPrompt || DEFAULT_UPLOAD_PROMPT;
    return imagePrompt;
  }, []);
  const attachmentUuid = image && image.attachmentUuid;
  const attachment = useAttachment(attachmentUuid, "uuid");

  const { loading: loadingAttachments } = ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });
  const loading = saving || loadingAttachments;
  const hasImg = image && image.src;

  /**
   * Automatically save the attachment after picker upload
   * - Then set the result value in the form
   * @param {Array} files
   */
  const pickerDoneHandler = (files) => {
    const data = prepareCreateAttachments([
      formatUploadedAttachment({
        attachment: {
          uuid: getUUID()
        },
        context: ATTACHMENT_CONTEXT_TYPE.CONTENT,
        file: files[0]
      })
    ]);
    setSaving(true);

    actions.createAttachment(
      {
        resourceID,
        resourceUUID,
        resource,
        data: data.attachments[0]
      },
      /**
       *  Attachment response object
       * @param {Object} err
       * @param {Object} responseData<uuid, data.url>
       */
      (err, responseData) => {
        setSaving(false);

        if (err) {
          setError(err);
        } else {
          setError(null);

          const updatedImage = prepareSchemaImageFromAttachment(
            image,
            responseData
          );
          setFieldValue(name, updatedImage);

          const isTouched = get(touched, name);
          if (!isTouched) {
            setFieldTouched(name, true);
          }
        }
      }
    );
  };
  const pickerProps = {
    pickerOptions: PICKER_OPTIONS.IMAGES,
    prompts: prompt,
    icon: "upload",
    onPickerDone: pickerDoneHandler,
    image: {
      className: "br-100 center",
      style: DIMENSIONS
    }
  };
  if (hasImg) {
    pickerProps.src = image.src;
  }
  if (attachment) {
    pickerProps.size = attachment.data.size;
    pickerProps.mimetype = attachment.data.mimetype;
  }

  const imageContent = <FilePicker {...pickerProps} />;

  return (
    <div className="ph3 bg-white bh--inset flex flex-column">
      <div className="flex flex-row justify-between items-center pv3">
        <label className="f6 mb2 dib fw6">Image</label>
        {hasImg && (
          <HoverIconButton
            tooltip="Clear"
            type="refresh"
            color={COLORS.blue}
            onClick={() => {
              setFieldValue(name, { src: "", attachmentUuid: "", url: "" });
              const isTouched = get(touched, name);
              if (!isTouched) {
                setFieldTouched(name, true);
              }
            }}
          />
        )}
      </div>
      <div className="flex flex-column w-100">
        {loading ? (
          <div
            style={IMAGE_LOADER_STYLE}
            className="w-100 tc flex items-center justify-center"
          >
            <LoadingSpinner customClasses={{ ring: "blue" }} />
          </div>
        ) : (
          <Fragment>
            {imageContent}
            {hasImg && (
              <div className="f7 pv3 tc">{`${formFactorAction()} to update image`}</div>
            )}
          </Fragment>
        )}
      </div>
      {error && (
        <div className="f6 w-100 self-center pt2 tl color-error">{error}</div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createAttachment,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ImageField);
